import React from 'react';
import { useLocalizedStrings } from '../../../Localization';
import { ActionsDropdown, Badge, Button, Card, CardBody, CardHeader, Icon } from '@aidkitorg/component-library';
import type * as survey from '@aidkitorg/types/lib/survey';
import type { SupportedLanguage } from '@aidkitorg/i18n/lib';
import { useDashboardDownload } from '../hooks/useDashboardDownload';

interface DownloadOnlyCardProps {
  component: {
    title?: string | survey.Text;
    download?: { filename?: string; downloadOnly?: boolean; asyncDownload?: boolean; downloadPath?: string };
  };
  dashboard: string | survey.Dashboard;
  lang: SupportedLanguage;
  /** Static widget ID from the dashboard widget library */
  staticWidgetId?: string;
  /** When true, shows delete action in card header */
  isEditMode?: boolean;
  /** Callback to delete this component from the dashboard */
  onDelete?: () => void;
}

/**
 * Renders a download-only Applicant Table as a card with a download button.
 * Uses the same Card/CardHeader/CardBody pattern as other dashboard component cards.
 */
export const DownloadOnlyCard: React.FC<DownloadOnlyCardProps> = ({ component, dashboard, lang, staticWidgetId, isEditMode, onDelete }) => {
  const L = useLocalizedStrings();

  const title = typeof component.title === 'string'
    ? component.title
    : component.title?.en || 'Download';
  const filename = component.download?.filename || 'download';

  const { downloadInProgress, handleDownload } = useDashboardDownload({
    filename,
    downloadPath: component.download?.downloadPath || '',
    asyncDownload: component.download?.asyncDownload,
    dashboard,
    lang,
  });

  const deleteAction = isEditMode && onDelete ? (
    <ActionsDropdown
      actions={[{
        label: L.admin_panel.dashboards.delete,
        onClick: onDelete,
        icon: 'TrashIcon',
        variant: 'warn' as const,
      }]}
      item={component}
    />
  ) : undefined;

  return (
    <Card className="rounded-md h-full w-full">
      <CardHeader
        title={
          <div className="flex flex-wrap items-center gap-2 text-sm px-4 py-2">
            {title}
            {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
          </div>
        }
        actionButton={deleteAction}
      />
      <CardBody className="flex-1 flex flex-col items-center justify-center gap-3">
        <span className="text-sm text-gray-500">{filename}.csv</span>
        <Button
          variant="secondary"
          loading={downloadInProgress}
          onClick={handleDownload}
          icon={<Icon name="ArrowDownTrayIcon" type="solid" role="decorative" />}
        >
          {L.dashboard.download}
        </Button>
      </CardBody>
    </Card>
  );
};
